import { useMemo } from 'react';

import { CUSTOMERS_WALL_QUICKFILTER_DEFAULT, MODELS_WALL_QUICKFILTER_DEFAULT } from '@configs/WALLS_CONFIG';

import { TAG_FILTER_MODE_DEFAULT } from '@libs/NwUtils';

import { useActiveShowSeasons } from '@hooks/API/ShowSeasons/UseShowSeasons';
import useUserDepartments from '@hooks/CachedLists/UseUserDepartments';

import { useContactsSelector } from '@components/ContactsSelector/Common/ContactsSelectorContext';
import useSelectedFilterDisplay from '@components/ContactsSelector/Common/UseSelectedFilterDisplay';

import { getSeasonDisplayName } from '@containers/Booking/ShowSeasons/showSeasonsHelpers';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

export const LIST_TYPES = {
    all: { icon: byPrefixAndName.fasl['bolt'], label: 'quick selection' },
    'smart-lists': { icon: byPrefixAndName.fasl['filters'], label: 'smart list' },
    folders: { icon: byPrefixAndName.fasl['folder'], label: 'folder' },
    project: { icon: byPrefixAndName.fasl['layer-group'], label: 'project' },
};

// Stesse voci del RadioButtonGroup in CustomerQuickFilters
const CUSTOMER_CATEGORY_LABELS = {
    0: 'all types',
    1: 'clients',
    2: 'agencies',
    3: 'services',
};

/**
 * Cosa è attualmente selezionato nel selettore contatti: tipo di lista, suo nome e
 * filtri realmente applicati. Usato dal riepilogo compatto su mobile e dal badge che
 * lo accompagna, così i due non possono divergere.
 *
 * Ogni tipo di lista usa una sola sorgente di filtri: i quick filters valgono solo in
 * 'all', i filtri della smart list solo in 'smart-lists' (vedi ModelsSelector /
 * CustomersSelector). Mostrare gli altri significherebbe elencare filtri che in quel
 * momento non stanno filtrando niente.
 */
const useContactsFiltersSummary = () => {
    const { context, selection, global } = useContactsSelector();
    const { activeSetName, filters, listType, quickFilters } = selection;
    const { itemType } = context;
    const { filtersBySection } = global;

    const { departments } = useUserDepartments();
    const { data: activeShowSeasons } = useActiveShowSeasons();
    const getFilterDisplay = useSelectedFilterDisplay({ firstMetricOnly: true });

    // I quick filters non stanno nel set dei filtri attivi: si riconoscono confrontandoli
    // con i default della wall.
    const quickFilterChips = useMemo(() => {
        const chips = [];
        if (!quickFilters || listType !== 'all') {
            return chips;
        }
        const defaults = itemType === 'model' ? MODELS_WALL_QUICKFILTER_DEFAULT : CUSTOMERS_WALL_QUICKFILTER_DEFAULT;

        if (quickFilters.onlymine) {
            chips.push('only mine');
        }
        if (quickFilters.tagFilter && quickFilters.tagFilter.length > 0) {
            // il separatore dice la modalita': con piu' tag "a, b" non direbbe se servono entrambi
            chips.push(quickFilters.tagFilter.join(` ${quickFilters.tagFilterMode || TAG_FILTER_MODE_DEFAULT} `));
        }

        if (itemType === 'model') {
            if (quickFilters.intown && quickFilters.intown !== defaults.intown) {
                chips.push(quickFilters.intown === 'in' ? 'in town' : 'out of town');
            }
            if (quickFilters.gender && quickFilters.gender !== defaults.gender) {
                chips.push(quickFilters.gender.replace('-', ' '));
            }
            if (quickFilters.departmentFilter) {
                const department = departments.find((d) => d.ID === quickFilters.departmentFilter);
                chips.push(department ? department.Name : 'department');
            }
            if (quickFilters.showSeasonFilter) {
                const season =
                    activeShowSeasons && activeShowSeasons.find((s) => s.ID === quickFilters.showSeasonFilter);
                chips.push(season ? getSeasonDisplayName(season) : 'show season');
            }
        } else if (quickFilters.category !== undefined && quickFilters.category !== defaults.category) {
            chips.push(CUSTOMER_CATEGORY_LABELS[quickFilters.category] || 'type');
        }

        return chips;
    }, [quickFilters, listType, itemType, departments, activeShowSeasons]);

    const filterChips = useMemo(() => {
        if (listType !== 'smart-lists') {
            return [];
        }
        return filters.active
            .map((filter) => {
                // La formattazione dei valori tocca liste cachate e conversioni: qui la barra
                // è sempre a schermo, quindi un filtro non formattabile degrada alla sola etichetta
                // invece di far cadere tutto il selettore.
                try {
                    const display = getFilterDisplay(filter);
                    if (!display) {
                        return null;
                    }
                    return display.values.length > 0
                        ? `${display.label}: ${display.values.join(' / ')}`
                        : display.label;
                } catch (error) {
                    console.error('Filter summary error => ', error);
                    return filter.name.replace(/_/g, ' ');
                }
            })
            .filter(Boolean);
        // getFilterDisplay è ricreata a ogni render: le dipendenze reali sono i filtri attivi
        // e la configurazione da cui l'hook legge le etichette
    }, [filters.active, filtersBySection, listType]);

    const chips = [...quickFilterChips, ...filterChips];

    // Il nome accanto al tipo: la smart list o la cartella attiva, oppure — in selezione
    // rapida senza filtri — cosa si sta effettivamente guardando.
    const listName = listType === 'all' ? (chips.length === 0 ? `all ${itemType}s` : null) : activeSetName;

    return {
        listTypeInfo: LIST_TYPES[listType] || LIST_TYPES.all,
        listName,
        chips,
    };
};

export default useContactsFiltersSummary;
